import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { FilterOption, UserFilterOption } from '../types';

// Default organizations to exclude by name.
// MUST stay in sync with the backend canon (constants/analytics_canon.py → EXCLUDED_ORG_NAMES),
// which the MCP schema doc and chat-with-usage prompt also derive from, so the app and the
// MCP report the same numbers (PEL-1823). TS can't import the Python module — update both.
const DEFAULT_EXCLUDED_ORG_NAMES = ['PellesAI', 'Webiya', 'pelles.ai', 'pelles_demo', 'pelles_cs', 'pelles-ae'];

// Helper to compute effective excluded user IDs
function computeEffectiveExcludedUserIds(
  excludedOrgIds: string[],
  excludedUserIds: string[],
  availableUsers: UserFilterOption[]
): string[] {
  const excludedSet = new Set(excludedUserIds);

  // Add all users that belong to excluded orgs
  for (const user of availableUsers) {
    if (user.orgId && excludedOrgIds.includes(user.orgId)) {
      excludedSet.add(user.id);
    }
  }

  return Array.from(excludedSet);
}

interface ExclusionsState {
  excludedOrgIds: string[];
  excludedUserIds: string[];
  effectiveExcludedUserIds: string[]; // Computed and stored
  availableOrgs: FilterOption[];
  availableUsers: UserFilterOption[];
  isInitialized: boolean;

  setExcludedOrgIds: (orgIds: string[]) => void;
  setExcludedUserIds: (userIds: string[]) => void;
  setAvailableOrgs: (orgs: FilterOption[]) => void;
  setAvailableUsers: (users: UserFilterOption[]) => void;
  initializeDefaults: (orgs: FilterOption[]) => void;
  initializeAll: (orgs: FilterOption[], users: UserFilterOption[]) => void;
  clearExclusions: () => void;
}

export const useExclusionsStore = create<ExclusionsState>()(
  persist(
    (set, get) => ({
      excludedOrgIds: [],
      excludedUserIds: [],
      effectiveExcludedUserIds: [],
      availableOrgs: [],
      availableUsers: [],
      isInitialized: false,

      setExcludedOrgIds: (excludedOrgIds) => {
        const state = get();
        const effectiveExcludedUserIds = computeEffectiveExcludedUserIds(
          excludedOrgIds,
          state.excludedUserIds,
          state.availableUsers
        );
        set({ excludedOrgIds, effectiveExcludedUserIds });
      },

      setExcludedUserIds: (excludedUserIds) => {
        const state = get();
        const effectiveExcludedUserIds = computeEffectiveExcludedUserIds(
          state.excludedOrgIds,
          excludedUserIds,
          state.availableUsers
        );
        set({ excludedUserIds, effectiveExcludedUserIds });
      },

      setAvailableOrgs: (availableOrgs) => set({ availableOrgs }),

      setAvailableUsers: (availableUsers) => {
        const state = get();
        const effectiveExcludedUserIds = computeEffectiveExcludedUserIds(
          state.excludedOrgIds,
          state.excludedUserIds,
          availableUsers
        );
        set({ availableUsers, effectiveExcludedUserIds });
      },

      initializeDefaults: (orgs) => {
        const state = get();
        // Only initialize defaults if not already initialized
        if (!state.isInitialized) {
          // Find org IDs matching default excluded org names
          const defaultExcludedIds = orgs
            .filter((org) =>
              DEFAULT_EXCLUDED_ORG_NAMES.some(
                (name) => org.name.toLowerCase() === name.toLowerCase()
              )
            )
            .map((org) => org.id);

          const effectiveExcludedUserIds = computeEffectiveExcludedUserIds(
            defaultExcludedIds,
            state.excludedUserIds,
            state.availableUsers
          );

          set({
            excludedOrgIds: defaultExcludedIds,
            effectiveExcludedUserIds,
            isInitialized: true,
          });
        }
      },

      // Initialize everything atomically to prevent multiple state updates
      initializeAll: (orgs, users) => {
        const state = get();

        // Determine excluded org IDs (use existing if already initialized, else compute defaults)
        let excludedOrgIds = state.excludedOrgIds;
        if (!state.isInitialized) {
          excludedOrgIds = orgs
            .filter((org) =>
              DEFAULT_EXCLUDED_ORG_NAMES.some(
                (name) => org.name.toLowerCase() === name.toLowerCase()
              )
            )
            .map((org) => org.id);
        }

        const effectiveExcludedUserIds = computeEffectiveExcludedUserIds(
          excludedOrgIds,
          state.excludedUserIds,
          users
        );

        set({
          availableOrgs: orgs,
          availableUsers: users,
          excludedOrgIds,
          effectiveExcludedUserIds,
          isInitialized: true,
        });
      },

      clearExclusions: () =>
        set({
          excludedOrgIds: [],
          excludedUserIds: [],
          effectiveExcludedUserIds: [],
        }),
    }),
    {
      name: 'product-dashboard-exclusions',
      partialize: (state) => ({
        excludedOrgIds: state.excludedOrgIds,
        excludedUserIds: state.excludedUserIds,
        // NOTE: isInitialized is intentionally NOT persisted
        // This ensures effectiveExcludedUserIds is computed before any fetches run
      }),
    }
  )
);
